/**
 * barFreshness — pure helper for measuring how recent a bars[] array is.
 *
 * The live Coach surfaces (Counter-Bias Whisper, Setups Firing Now) read
 * from `barDataService.fetchBars`. That source caches per-day, with a TTL
 * on the current trading day. If JournifiBars (the NinjaTrader bar writer)
 * stops appending new bars — for any reason — the cache will return the
 * last-known good bars and the live surfaces will silently keep firing
 * patterns on stale data.
 *
 * That's bad for trust. This helper lets every live surface check + display
 * how old the data it's about to analyze really is, so a stalled feed is
 * visible at a glance instead of being inferred from "wait, why hasn't this
 * card updated in a while?"
 *
 * Pure function. No side effects.
 */

// Defaults tuned for the 5m timeframe (the only timeframe the live surfaces
// currently poll). Each threshold answers a different question:
//
//   freshThresholdSec    — "the latest bar is within the current bar period."
//                           At 5m bars, anything ≤ 6 min old is still firmly
//                           inside the most-recent closed bar's expected
//                           append window. Green.
//
//   staleThresholdSec    — "we should have at least one new bar by now."
//                           After 15 min on 5m, we'd expect 2-3 new bars to
//                           have appended. If they haven't, something might
//                           be lagging. Amber — show caution.
//
//   downThresholdSec     — "the feed is materially broken." After 60 min,
//                           we should have seen 12 new 5m bars. Zero means
//                           the bar writer is silent — don't show the live
//                           card at all. Red — render a "feed appears down"
//                           message instead.
export const DEFAULT_FRESH_SEC = 6 * 60;
export const DEFAULT_STALE_SEC = 15 * 60;
export const DEFAULT_DOWN_SEC  = 60 * 60;

export const STATUS = Object.freeze({
  EMPTY:      'empty',       // No bars at all
  FRESH:      'fresh',       // ≤ freshThresholdSec
  STALE:      'stale',       // freshThresholdSec < age ≤ staleThresholdSec
  VERY_STALE: 'very-stale',  // staleThresholdSec < age ≤ downThresholdSec
  DOWN:       'down',        // age > downThresholdSec
});

/**
 * Format an age in seconds as a compact "X ago" label.
 *   < 60s   → "just now"
 *   < 60m   → "Nm ago"
 *   < 24h   → "Nh Mm ago"
 *   ≥ 24h   → "Nd ago"
 */
export const formatAgeLabel = (ageSec) => {
  if (!Number.isFinite(ageSec) || ageSec < 0) return '—';
  const s = Math.floor(ageSec);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  const remM = m - h * 60;
  if (h < 24) return remM > 0 ? `${h}h ${remM}m ago` : `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

/**
 * Format the last-bar Unix timestamp as a wall-clock HH:MM in the local
 * timezone. Used for the "Latest bar 13:45" prefix on freshness pills.
 * Returns "—" when the timestamp isn't a number.
 *
 * NOTE: this is local-clock display only — not the canonical ET storage
 * frame used by trade.entryTime. The card pairs it with the relative "Xm
 * ago" so the wall-clock value is purely a quick-glance reference, and the
 * source of truth is the age.
 */
export const formatBarTimeLabel = (unixSec) => {
  if (!Number.isFinite(unixSec)) return '—';
  const d = new Date(unixSec * 1000);
  if (Number.isNaN(d.getTime())) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

/**
 * Compute freshness for a bars array.
 *
 * @param {Object} params
 * @param {Array}  params.bars     Bar array (last element is most recent).
 * @param {number} [params.nowMs]  "Now" in ms. Defaults to Date.now().
 *                                 Injected for testability — every test
 *                                 should pass an explicit nowMs.
 * @param {number} [params.freshThresholdSec]  Defaults to DEFAULT_FRESH_SEC.
 * @param {number} [params.staleThresholdSec]  Defaults to DEFAULT_STALE_SEC.
 * @param {number} [params.downThresholdSec]   Defaults to DEFAULT_DOWN_SEC.
 *
 * @returns {Object}
 *   {
 *     status:        one of STATUS values
 *     lastBarTime:   unix sec of latest bar, or null when empty/invalid
 *     ageSec:        seconds since latest bar, or null when empty/invalid
 *     ageLabel:      "8m ago" / "just now" / "1h 23m ago" / "—"
 *     barTimeLabel:  local "HH:MM" of latest bar, or "—"
 *     barCount:      bars.length (or 0)
 *   }
 *
 * When bars is empty/invalid, returns status='empty' with null age fields.
 * When the latest bar has an invalid time, also returns 'empty' (we can't
 * trust freshness on a corrupt bar).
 */
export const computeBarFreshness = ({
  bars,
  nowMs = Date.now(),
  freshThresholdSec = DEFAULT_FRESH_SEC,
  staleThresholdSec = DEFAULT_STALE_SEC,
  downThresholdSec  = DEFAULT_DOWN_SEC,
} = {}) => {
  const empty = {
    status:       STATUS.EMPTY,
    lastBarTime:  null,
    ageSec:       null,
    ageLabel:     '—',
    barTimeLabel: '—',
    barCount:     0,
  };

  if (!Array.isArray(bars) || bars.length === 0) return empty;

  const last = bars[bars.length - 1];
  const lastTime = Number(last?.time);
  if (!Number.isFinite(lastTime)) {
    return { ...empty, barCount: bars.length };
  }

  const nowSec = Math.floor(Number(nowMs) / 1000);
  if (!Number.isFinite(nowSec)) {
    // Defensive — should never happen with a real Date.now()
    return { ...empty, lastBarTime: lastTime, barTimeLabel: formatBarTimeLabel(lastTime), barCount: bars.length };
  }

  // Age is clamped at 0 — bars from the FUTURE (clock skew between bar source
  // and host) shouldn't read as "negative seconds old"; that's still fresh.
  const ageSec = Math.max(0, nowSec - lastTime);

  let status;
  if (ageSec <= freshThresholdSec) status = STATUS.FRESH;
  else if (ageSec <= staleThresholdSec) status = STATUS.STALE;
  else if (ageSec <= downThresholdSec) status = STATUS.VERY_STALE;
  else status = STATUS.DOWN;

  return {
    status,
    lastBarTime:  lastTime,
    ageSec,
    ageLabel:     formatAgeLabel(ageSec),
    barTimeLabel: formatBarTimeLabel(lastTime),
    barCount:     bars.length,
  };
};

export default computeBarFreshness;
